/**
 * kult-gift-builder.js — Konfigurator „Kult-Box selbst befüllen“ (Stand 29.09.2026).
 * Slots (max data-max) für Kaffee/Tee/Confiserie/Zubehör/Gutschein, eigener 5. Platz für EINE
 * Grußkarte (data-kind="card") mit Grußtext (max. 500 Zeichen), Anlass über Bild-Kacheln + Dropdown
 * (Default data-occasion-default, Box-Bild wechselt aus data-gb-occasion-images), Tabs, Filter-Chips,
 * Zubereitungs-Pills für Kaffee (keine Vorauswahl, Pflicht beim Warenkorb), Live-Summe mit
 * Versandkosten-Fortschritt (data-free-cents), Zustand in der URL (?box=&zub=&anlass=&karte=),
 * Warenkorb über /cart/add.js mit Gruppen-Eigenschaft _kultbox (Box-Zeile trägt „Anlass“,
 * Karten-Zeile „Grußtext“); öffnet den Drawer über das Event "kult:cart:add" (assets/kult-cart.js).
 */
(function () {
  'use strict';

  function money(cents) {
    return (cents / 100).toFixed(2).replace('.', ',') + ' €';
  }
  function moneyShort(cents) {
    return cents % 100 === 0 ? (cents / 100) + ' €' : money(cents);
  }

  /* Anlass-Label → Key wie im Tag anlass_<key> („Dankeschön“ → dankeschoen, „Alles Gute“ → alles-gute) */
  function occasionKey(label) {
    return String(label || '').toLowerCase()
      .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
      .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  class KultGiftBuilder extends HTMLElement {
    connectedCallback() {
      this.syncDetailsRows();
      this.max = parseInt(this.dataset.max, 10) || 6;
      /* Mindestinhalt (Philip 29.10.2026): eine Kult-Box hat 3 bis 6 Produkte, die Grußkarte zählt nicht mit */
      this.min = Math.min(parseInt(this.dataset.min, 10) || 1, this.max);
      this.boxPrice = parseInt(this.dataset.boxPrice, 10) || 0;
      this.freeCents = parseInt(this.dataset.freeCents, 10) || 0;
      this.freeClubCents = parseInt(this.dataset.freeClubCents, 10) || 0;
      this.freeStatusCents = parseInt(this.dataset.freeStatusCents, 10) || 0;
      this.freeMixCents = parseInt(this.dataset.freeMixCents, 10) || 0;
      this.hasStatus = this.dataset.hasStatus === 'true';   /* Kult Status vorhanden: Grenze der Stufe bei jeder Bestellung, Gold = 0 */
      this.cartTotal = parseInt(this.dataset.cartTotal, 10) || 0;
      this.cartHasClub = this.dataset.cartHasClub === 'true';
      /* Abo hat Vorrang (09.10.2026): kleinste Abo-Lieferung in Gramm (-1 = kein Kult-Abo im Warenkorb), Grenze je Lieferung */
      this.aboG = parseInt(this.dataset.aboG, 10);
      if (isNaN(this.aboG)) this.aboG = -1;
      this.aboFreiG = parseInt(this.dataset.aboFreiG, 10) || 0;
      /* Gemischter Warenkorb (10.10.2026): versandpflichtiger Warenwert im Warenkorb inkl. Kult-Abo (kult-cart-versand [15]) */
      this.cartShipCents = parseInt(this.dataset.cartShipCents, 10) || 0;
      this.occasionDefault = this.dataset.occasionDefault || '';
      this.initialParams = new URLSearchParams(window.location.search);
      this.items = [];
      this.card = null;
      this.greeting = '';
      this.occasionSelect = this.querySelector('[data-gb-occasion-select]');
      this.occasion = this.occasionSelect ? this.occasionSelect.value : this.occasionDefault;
      this.occasionImages = {};
      try { this.occasionImages = JSON.parse((this.querySelector('[data-gb-occasion-images]') || {}).textContent || '{}'); } catch (err) { this.occasionImages = {}; }
      this.boxImage = this.querySelector('[data-gb-box-image]');
      this.boxTitle = this.querySelector('[data-gb-box-title]');
      this.occasionPhrases = {};
      try { this.occasionPhrases = JSON.parse((this.querySelector('[data-gb-occasion-phrases]') || {}).textContent || '{}'); } catch (err) { this.occasionPhrases = {}; }
      /* Kult-Box je Anlass (07.10.2026): { key: { v, p, t, img } } aus Kollektion „kult-boxen“, Fallback _default */
      this.boxes = {};
      try { this.boxes = JSON.parse((this.querySelector('[data-gb-boxes]') || {}).textContent || '{}'); } catch (err) { this.boxes = {}; }
      this.boxVariantId = parseInt(this.dataset.boxVariant, 10) || 0;
      this.boxPriceEl = this.querySelector('[data-gb-box-price]');
      const activeGrind = this.querySelector('[data-gb-grind].is-active');
      this.grind = activeGrind ? activeGrind.dataset.gbGrind : '';
      this.slotsEl = this.querySelector('[data-gb-slots]');
      this.cardSlot = this.querySelector('[data-gb-card-slot]');
      this.cardEmptyHtml = this.cardSlot ? this.cardSlot.innerHTML : '';
      this.greetingWrap = this.querySelector('[data-gb-greeting]');
      this.greetingInput = this.querySelector('[data-gb-greeting-text]');
      this.greetingCount = this.querySelector('[data-gb-greeting-count]');
      this.statusEl = this.querySelector('[data-gb-status]');
      this.addBtn = this.querySelector('[data-gb-submit]');
      this.errorEl = this.querySelector('[data-gb-error]');
      this.grindNote = this.querySelector('[data-gb-grind-note]');
      this.chooser = this.querySelector('[data-gb-chooser]');
      this.boxEl = this.querySelector('.kult-gb__box');
      this.chooserOpen = false;
      this.addEventListener('click', this.onClick.bind(this));
      let resizeTimer = null;
      window.addEventListener('resize', () => { clearTimeout(resizeTimer); resizeTimer = setTimeout(() => this.render(), 150); });
      if (this.occasionSelect) this.occasionSelect.addEventListener('change', () => this.setOccasion(this.occasionSelect.value));
      if (this.greetingInput) {
        this.greetingInput.addEventListener('input', () => {
          try { if (this.greetingInput.value) sessionStorage.setItem('kult:gift-gruss', this.greetingInput.value); else sessionStorage.removeItem('kult:gift-gruss'); } catch (e) {}
          this.greeting = this.greetingInput.value.slice(0, 500);
          if (this.greetingCount) this.greetingCount.textContent = this.greeting.length + '/500 Zeichen';
          this.updateCardPreview();
        });
      }
      /* Details einer Karte klappen ab 960 px alle Karten des Panels mit auf/zu (Philip 29.09.2026) */
      this.addEventListener('toggle', (e) => {
        const details = e.target;
        if (!details.matches || !details.matches('[data-gb-card] details') || this.syncingDetails) return;
        if (!window.matchMedia('(min-width: 960px)').matches) return;
        const panel = details.closest('[data-gb-panel]');
        if (!panel) return;
        this.syncingDetails = true;
        panel.querySelectorAll('[data-gb-card] details').forEach((d) => { if (d !== details) d.open = details.open; });
        this.syncingDetails = false;
      }, true);
      /* Warenkorb ändert sich (Drawer): Versand-Fortschritt neu rechnen */
      /* Links mit data-gifts-tab (z. B. Partner-Section) öffnen eine Kategorie; ?kat= in der URL ebenso */
      document.addEventListener('click', (e) => {
        const link = e.target.closest('[data-gifts-tab]');
        if (!link || this.contains(link)) return;
        e.preventDefault();
        this.showTab(link.dataset.giftsTab);
        const chooser = this.querySelector('.kult-gb__chooser');
        if (chooser) chooser.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      });
      const kat = this.initialParams.get('kat');
      if (kat && this.querySelector('[data-gb-tab="' + kat.replace(/[^a-z]/g, '') + '"]')) this.showTab(kat.replace(/[^a-z]/g, ''));
      document.addEventListener('kult:cart:updated', (e) => this.syncCart(e.detail && e.detail.cart));
      document.addEventListener('kult:cart:add', (e) => this.syncCart(e.detail && e.detail.cart));
      this.restoreGrindFromUrl();
      this.restoreFromUrl();
      /* Grußtext übersteht den Ausflug zur Produktseite (Sitzungsspeicher kult:gift-gruss, bis zum Schließen des Tabs; Datenschutz Ziffer 12) */
      try {
        const g = sessionStorage.getItem('kult:gift-gruss');
        if (g && this.greetingInput && !this.greetingInput.value) { this.greetingInput.value = g; this.greetingInput.dispatchEvent(new Event('input')); }
      } catch (e) {}
      this.render();
    }

    onClick(e) {

      if (e.target.closest('[data-gb-add], [data-gb-remove], [data-gb-swap]')) this.autofillNote = '';
      const tab = e.target.closest('[data-gb-tab]');
      if (tab) return this.showTab(tab.dataset.gbTab);
      const chip = e.target.closest('[data-gb-filter]');
      if (chip) return this.setFilter(chip);
      const grind = e.target.closest('[data-gb-grind]');
      if (grind) return this.setGrind(grind);
      const occasion = e.target.closest('[data-gb-occasion]');
      if (occasion) return this.setOccasion(occasion.dataset.gbOccasion, true);
      const dialog = this.querySelector('[data-gb-card-dialog]');
      if (e.target.closest('[data-gb-card-zoom]')) return dialog && dialog.showModal();
      if (e.target.closest('[data-gb-card-zoom-close]') || e.target === dialog) return dialog.close();
      if (e.target.closest('[data-gb-card-promo]')) return this.promoBtn && this.setCard(this.promoBtn);
      if (e.target.closest('[data-gb-card-open]')) return this.openCards();
      if (e.target.closest('[data-gb-open-chooser]')) return this.openChooser();
      if (e.target.closest('[data-gb-close-chooser]')) return this.closeChooser(true);
      const add = e.target.closest('[data-gb-add]');
      if (add) return add.dataset.kind === 'card' ? this.setCard(add) : this.addItem(add);
      const removeCard = e.target.closest('[data-gb-remove-card]');
      if (removeCard) return this.removeCard();
      const grindNote = e.target.closest('[data-gb-grind-note]');
      if (grindNote && grindNote.classList.contains('is-warn')) return this.jumpToGrind();
      const remove = e.target.closest('[data-gb-remove]');
      if (remove) return this.removeItem(parseInt(remove.dataset.gbRemove, 10));
      if (e.target.closest('[data-gb-submit]')) return this.submit();
    }

    showTab(cat) {
      this.querySelectorAll('[data-gb-tab]').forEach((t) => {
        const on = t.dataset.gbTab === cat;
        t.classList.toggle('is-active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      this.querySelectorAll('[data-gb-panel]').forEach((p) => { p.hidden = p.dataset.gbPanel !== cat; });
      const activeTab = this.querySelector('[data-gb-tab="' + cat + '"]');
      if (activeTab && activeTab.scrollIntoView) activeTab.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'auto' });
    }

    /* Nur scrollen, wenn das Ziel nicht (ganz) im Viewport liegt */
    scrollToIfNeeded(el, block) {
      if (!el) return;
      const r = el.getBoundingClientRect();
      const inView = r.top >= 0 && r.top < window.innerHeight * 0.6;
      if (inView) return;
      el.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: block || 'start' });
    }

    setGrindError(on) {
      const group = this.querySelector('[data-gb-grind-group]');
      const msg = this.querySelector('[data-gb-grind-error]');
      if (group) group.classList.toggle('is-error', !!on);
      if (msg) msg.hidden = !on;
    }

    setFilter(chip) {
      const group = chip.dataset.gbFilter;
      const panel = chip.closest('[data-gb-panel]');
      panel.querySelectorAll('[data-gb-filter="' + group + '"]').forEach((c) => {
        const on = c === chip;
        c.classList.toggle('is-active', on);
        c.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      this.applyFilters(panel);
    }

    /* Filter anwenden; Aromen-Chips zeigen nur Aromen, die es in der aktuellen Kategorie-Auswahl gibt
       (Philip 02.10.2026). Fällt das aktive Aroma weg, springt die Auswahl auf „Alle Aromen“. */
    applyFilters(panel) {
      const active = {};
      panel.querySelectorAll('[data-gb-filter].is-active').forEach((c) => { if (c.dataset.gbValue) active[c.dataset.gbFilter] = c.dataset.gbValue; });
      const cards = Array.from(panel.querySelectorAll('[data-gb-card]'));
      const matches = (card, keys) => keys.every((key) => (card.dataset[key] || '').split(' ').indexOf(active[key]) !== -1);
      const otherKeys = Object.keys(active).filter((k) => k !== 'aroma');
      const available = new Set();
      cards.forEach((card) => {
        if (matches(card, otherKeys)) (card.dataset.aroma || '').split(' ').filter(Boolean).forEach((a) => available.add(a));
      });
      let activeAromaGone = false;
      panel.querySelectorAll('[data-gb-filter="aroma"]').forEach((chip) => {
        const val = chip.dataset.gbValue;
        if (!val) return;
        const show = available.has(val);
        chip.hidden = !show;
        if (!show && chip.classList.contains('is-active')) {
          chip.classList.remove('is-active');
          chip.setAttribute('aria-pressed', 'false');
          activeAromaGone = true;
        }
      });
      if (activeAromaGone) {
        const all = panel.querySelector('[data-gb-filter="aroma"][data-gb-value=""]');
        if (all) { all.classList.add('is-active'); all.setAttribute('aria-pressed', 'true'); }
        delete active.aroma;
      }
      cards.forEach((card) => { card.hidden = !matches(card, Object.keys(active)); });
    }

    setGrind(btn) {
      this.grind = btn.dataset.gbGrind;
      this.querySelectorAll('[data-gb-grind]').forEach((b) => {
        const on = b === btn;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      this.items.forEach((it) => { if (it.variants) it.variant = it.variants[this.grind] || it.variant; });
      this.flash('');
      this.setGrindError(false);
      this.render();
    }

    /* Anlass: Kacheln und Dropdown synchron, Box-Bild wechselt; ohne Wahl gilt der Standard („Einfach so“) */
    setOccasion(label, fromTile) {
      this.occasion = label || this.occasionDefault;
      this.querySelectorAll('[data-gb-occasion]').forEach((b) => {
        const on = b.dataset.gbOccasion === this.occasion;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
      if (this.occasionSelect && this.occasionSelect.value !== this.occasion) this.occasionSelect.value = this.occasion;
      if (this.boxTitle) {
        const phrase = this.occasionPhrases[this.occasion] || '';
        this.boxTitle.textContent = (this.boxTitle.dataset.titleBase || 'Kult-Box') + (phrase ? ' ' + phrase : '');
      }
      const box = this.boxes[occasionKey(this.occasion)] || this.boxes._default;
      if (box) {
        this.boxVariantId = box.v;
        this.boxPrice = box.p;
        this.boxProductTitle = box.t;
        if (this.boxPriceEl) this.boxPriceEl.textContent = money(box.p);
      }
      const boxImg = this.occasionImages[this.occasion] || (box && box.img);
      if (this.boxImage && boxImg) {
        this.boxImage.src = boxImg;
        this.boxImage.removeAttribute('srcset');
        this.boxImage.alt = 'Kult-Box – ' + this.occasion;
      }
      this.highlightCards();
      if (fromTile) this.scrollToIfNeeded(this.querySelector('.kult-gb__chooser'), 'start');
      this.flash('');
      this.render();
    }

    syncCart(cart) {
      if (!cart) return;
      this.cartTotal = cart.total_price || 0;
      this.cartHasClub = (cart.items || []).some((i) => !!i.selling_plan_allocation);
      this.cartShipCents = (cart.items || []).reduce((sum, i) => (i.requires_shipping === false ? sum : sum + (i.final_line_price || 0)), 0);
      /* Abo-Lieferung = Zeilen mit demselben Plan, Gramm aus der SKU KR-XXX-250|500|1000-YY */
      const plans = {};
      (cart.items || []).forEach((i) => {
        const sp = i.selling_plan_allocation && i.selling_plan_allocation.selling_plan;
        if (!sp) return;
        const parts = String(i.sku || '').split('-');
        plans[sp.id] = (plans[sp.id] || 0) + (parts.length === 4 && parts[0] === 'KR' ? (parseInt(parts[2], 10) || 0) * (i.quantity || 0) : 0);
      });
      const ids = Object.keys(plans);
      this.aboG = ids.length ? Math.min(...ids.map((k) => plans[k])) : -1;
      this.render();
    }

    /* Grußkarte, die zum Anlass passt, nach vorn und markieren (Tag anlass_<key>) */
    highlightCards() {
      const key = occasionKey(this.occasion);
      const grid = this.querySelector('[data-gb-panel="grusskarte"] [data-gb-grid]');
      if (!grid) return;
      grid.querySelectorAll('[data-gb-card]').forEach((card) => {
        const match = !!key && card.dataset.anlass === key;
        card.classList.toggle('is-match', match);
        if (match && grid.firstElementChild !== card) grid.prepend(card);
      });
      this.updateCardPromo(grid, key);
    }

    /* Leerer Grußkarten-Platz wirbt mit der Karte zum Anlass, sonst der allgemeinen Karte */
    updateCardPromo(grid, key) {
      const find = (k) => grid.querySelector('[data-gb-card][data-anlass="' + k + '"] [data-gb-add]');
      const btn = (key && find(key)) || find('einfach-so') || grid.querySelector('[data-gb-add]');
      if (!btn || !this.cardSlot) return;
      this.promoBtn = btn;
      const passend = !!key && find(key) === btn && key !== 'einfach-so';
      const box = document.createElement('div');
      box.className = 'kult-gb__card-promo';
      box.innerHTML = '<span class="kult-gb__card-promo-text"><strong></strong><small></small></span>' +
        '<button type="button" class="kult-gb__card-promo-add" data-gb-card-promo></button><button type="button" class="kult-gb__card-promo-other" data-gb-card-open>Andere Karte wählen</button>';
      box.querySelector('strong').textContent = btn.dataset.title;
      box.querySelector('small').textContent = (passend ? 'Passend zu deinem Anlass. ' : '') + 'Dein persönlicher Grußtext steht auf der Rückseite.';
      box.querySelector('[data-gb-card-promo]').textContent = 'Grußkarte hinzufügen · ' + money(parseInt(btn.dataset.price, 10) || 0);
      this.cardEmptyHtml = box.outerHTML;
      if (!this.card) this.cardSlot.innerHTML = this.cardEmptyHtml;
      this.updateCardPreview();
    }

    /* Vorder- und Rückseite der gewählten bzw. beworbenen Karte; Schriftgröße wie im Druck (lib/grusskarte.server.ts, 15/13/11,5/10,5 pt auf 298 pt Breite) */
    updateCardPreview() {
      const wrap = this.querySelector('[data-gb-card-preview]');
      if (!wrap) return;
      const src = this.card ? this.card.imgLarge : (this.promoBtn && this.promoBtn.dataset.imgLarge);
      wrap.hidden = !src;
      if (!src) return;
      const title = this.card ? this.card.title : this.promoBtn.dataset.title;
      this.querySelectorAll('[data-gb-card-front]').forEach((img) => {
        if (img.getAttribute('src') !== src) img.src = src;
        img.hidden = false;
        img.alt = title + ', Vorderseite';
      });
      const own = (this.card && this.greeting.trim()) || '';
      const text = own || wrap.dataset.example || '';
      const size = text.length <= 120 ? 5.03 : text.length <= 240 ? 4.36 : text.length <= 380 ? 3.86 : 3.52;
      this.querySelectorAll('[data-gb-card-text]').forEach((el) => { el.textContent = text; el.style.fontSize = size + 'cqw'; });
      this.querySelectorAll('[data-gb-card-text-label]').forEach((el) => { el.textContent = own ? 'Rückseite · dein Grußtext' : 'Rückseite · Beispieltext'; });
    }

    isMobile() {
      return !window.matchMedia('(min-width: 960px)').matches;
    }

    /* Mobil: Produktauswahl auf-/zuklappen (Desktop immer offen) */
    openChooser(tab) {
      this.chooserOpen = true;
      if (this.chooser) this.chooser.classList.add('is-open');
      const bar = this.querySelector('[data-gb-bar]');
      if (bar) bar.hidden = this.items.length === 0 && !this.card;
      if (tab) this.showTab(tab);
      /* Auch auf dem Desktop zur Auswahl scrollen (Philip 16.10.2026): der „Produkt hinzufügen“-Knopf sitzt in der Box-Spalte */
      if (this.chooser) this.chooser.scrollIntoView({ behavior: 'auto', block: 'start' });
    }
    closeChooser(scrollToBox) {
      this.chooserOpen = false;
      if (this.chooser) this.chooser.classList.remove('is-open');
      const bar = this.querySelector('[data-gb-bar]');
      if (bar && this.isMobile()) bar.hidden = true;
      if (scrollToBox && this.boxEl && this.isMobile()) {
        /* Ansicht wechseln: sofort (kein Smooth-Scroll, der im Horizon-Wrapper hängen bleibt) */
        this.boxEl.scrollIntoView({ behavior: 'auto', block: 'start' });
      }
    }

    /* Zur Zubereitung springen und den Fehler an den Pills zeigen */
    jumpToGrind() {
      if (this.isMobile()) this.openChooser();
      this.showTab('kaffee');
      this.setGrindError(true);
      const grindGroup = this.querySelector('[data-gb-grind-group]');
      if (grindGroup) grindGroup.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
      const firstGrind = this.querySelector('[data-gb-grind]');
      if (firstGrind) firstGrind.focus({ preventScroll: true });
    }

    /* CTA im Grußkarten-Platz öffnet die Kategorie Grußkarte */
    openCards() {
      if (this.isMobile()) this.openChooser();
      this.showTab('grusskarte');
      const panel = this.querySelector('[data-gb-panel="grusskarte"]');
      if (panel) {
        this.scrollToIfNeeded(panel, 'start');
        const first = panel.querySelector('[data-gb-add]');
        if (first) first.focus({ preventScroll: true });
      }
    }

    /* „Details“ öffnet und schließt beide Karten derselben Reihe (Later-Liste 22.10. Nr. 4): Reihe = gleicher offsetTop im Raster,
       so bleibt es bei jedem Spaltenwechsel richtig; toggle bubbelt nicht, daher Capture am Element. */
    syncDetailsRows() {
      let busy = false;
      this.addEventListener('toggle', (e) => {
        const d = e.target;
        if (busy || !(d instanceof HTMLDetailsElement) || !d.classList.contains('kult-plp-card__more')) return;
        const card = d.closest('[data-gb-card]'), grid = card && card.parentElement;
        if (!card || !grid) return;
        busy = true;
        grid.querySelectorAll(':scope > [data-gb-card]').forEach((other) => {
          if (other === card || other.hidden || other.offsetTop !== card.offsetTop) return;
          const od = other.querySelector('details.kult-plp-card__more');
          if (od && od.open !== d.open) od.open = d.open;
        });
        busy = false;
      }, true);
    }

    entryFromButton(btn) {
      let variants = null;
      try { variants = btn.dataset.variants ? JSON.parse(btn.dataset.variants) : null; } catch (err) { variants = null; }
      if (variants && !Object.keys(variants).length) variants = null;
      return {
        handle: btn.dataset.handle,
        title: btn.dataset.title,
        cat: btn.dataset.cat,
        price: parseInt(btn.dataset.price, 10) || 0,
        imgLarge: btn.dataset.imgLarge,
        img: btn.dataset.img || '',
        fill: btn.dataset.fill || '',
        variants: variants,
        variant: variants ? (variants[this.grind] || Object.values(variants)[0]) : parseInt(btn.dataset.variant, 10)
      };
    }

    addItem(btn) {
      if (this.items.length >= this.max) {
        this.flash('Deine Box ist voll. Entferne erst ein Produkt, um ein anderes hinzuzufügen.');
        return;
      }
      this.items.push(this.entryFromButton(btn));
      this.flash('');
      this.render();
      btn.classList.add('is-added');
      setTimeout(() => btn.classList.remove('is-added'), 600);
      if (this.isMobile()) this.closeChooser(true);
      /* Neu gefüllten Platz kurz hervorheben */
      const filled = this.slotsEl.querySelectorAll('[data-gb-slot]:not(.is-empty)');
      const last = filled[filled.length - 1];
      if (last) { last.classList.add('is-just-added'); setTimeout(() => last.classList.remove('is-just-added'), 1400); }
    }

    /* Grußkarte: eigener Platz, eine Karte je Box – eine zweite Wahl ersetzt die erste */
    setCard(btn) {
      this.card = this.entryFromButton(btn);
      this.flash('');
      this.render();
      btn.classList.add('is-added');
      setTimeout(() => btn.classList.remove('is-added'), 600);
      if (this.isMobile()) {
        this.closeChooser(false);
        if (this.greetingWrap) this.greetingWrap.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
      }
      if (this.greetingInput) this.greetingInput.focus({ preventScroll: true });
      /* Desktop: Grußtext-Feld über den klebenden Summenblock der Box holen */
      const scroller = this.greetingWrap && this.greetingWrap.closest('.kult-gb__box');
      const foot = scroller && scroller.querySelector('.kult-gb__foot');
      if (foot && !this.isMobile()) {
        const over = this.greetingWrap.getBoundingClientRect().bottom - foot.getBoundingClientRect().top + 12;
        if (over > 0) scroller.scrollTop += over;
      }
    }

    removeCard() {
      this.card = null;
      this.greeting = '';
      if (this.greetingInput) this.greetingInput.value = '';
      if (this.greetingCount) this.greetingCount.textContent = '0/500 Zeichen';
      this.flash('');
      this.render();
      this.addBtn.focus();
    }

    removeItem(index) {
      this.items.splice(index, 1);
      this.flash('');
      this.render();
      const first = this.slotsEl.querySelector('[data-gb-remove]');
      if (first) first.focus(); else this.addBtn.focus();
    }

    slotMarkup(it, meta, removeAttr, removeLabel) {
      const giftIcon = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="8" width="18" height="4" rx="1"/><path d="M12 8v13M5 12v9h14v-9M12 8c-2-3-6-3-6-1s3 1 6 1zm0 0c2-3 6-3 6-1s-3 1-6 1z"/></svg>';
      const thumb = it.img ? '<img src="' + it.img + '" alt="" width="48" height="48" loading="lazy">' : '<span class="kult-gb__slot-thumb-empty" aria-hidden="true">' + giftIcon + '</span>';
      /* Produktzeile klickbar (Philip 16.10.2026): PDP im selben Tab; die Box übersteht das über die URL-Params (syncUrl/restoreFromUrl) */
      const slotHandle = String(it.handle || '').replace(/[^a-z0-9-]/g, '');
      const slotTitle = slotHandle ? '<a class="kult-gb__slot-link" href="/products/' + slotHandle + '"><strong>' + it.title + '</strong></a>' : '<strong>' + it.title + '</strong>';
      return '<span class="kult-gb__slot-thumb">' + thumb + '</span>' +
        '<span class="kult-gb__slot-text">' + slotTitle + '<small>' + meta + '</small></span>' +
        '<span class="kult-gb__slot-price">' + money(it.price) + '</span>' +
        '<button type="button" class="kult-gb__slot-remove" ' + removeAttr + ' aria-label="' + removeLabel + '">×</button>';
    }

    render() {
      const slots = Array.from(this.slotsEl.querySelectorAll('[data-gb-slot]'));
      slots.forEach((slot, i) => {
        const it = this.items[i];
        slot.classList.toggle('is-empty', !it);
        if (!it) {
          slot.innerHTML = '<span class="kult-gb__slot-empty">Platz ' + (i + 1) + '</span>' +
            '<button type="button" class="kult-gb__slot-add" data-gb-open-chooser><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg><span>Produkt hinzufügen</span></button>';
          return;
        }
        const meta = it.cat + (it.fill ? ' · ' + it.fill : '') + (it.variants ? ' · ' + (this.grind || 'Zubereitung wählen') : '');
        slot.innerHTML = this.slotMarkup(it, meta, 'data-gb-remove="' + i + '"', it.title + ' entfernen');
      });
      if (this.cardSlot) {
        this.cardSlot.classList.toggle('is-empty', !this.card);
        this.cardSlot.innerHTML = this.card
          ? this.slotMarkup(this.card, 'Grußkarte', 'data-gb-remove-card', this.card.title + ' entfernen')
          : this.cardEmptyHtml;
      }
      if (this.greetingWrap) this.greetingWrap.hidden = !this.card;
      this.updateCardPreview();
      /* Karten mit „In der Box“ markieren */
      const inBox = {};
      this.items.forEach((it) => { inBox[it.handle] = true; });
      if (this.card) inBox[this.card.handle] = true;
      this.querySelectorAll('[data-gb-add]').forEach((btn) => {
        const on = !!inBox[btn.dataset.handle];
        if (!btn.dataset.label) btn.dataset.label = (btn.querySelector('span') || btn).textContent.trim();
        btn.classList.toggle('is-in-box', on);
        const span = btn.querySelector('span');
        if (span) span.textContent = on ? 'In der Box' : btn.dataset.label;
      });

      const itemsSum = this.items.reduce((acc, it) => acc + it.price, 0) + (this.card ? this.card.price : 0);
      const total = itemsSum + this.boxPrice;
      const free = this.max - this.items.length;
      this.querySelector('[data-gb-items-total]').textContent = money(itemsSum);
      this.querySelector('[data-gb-total]').textContent = money(total);
      const missing = this.min - this.items.length;
      if (missing > 0) this.statusEl.textContent = 'Noch mindestens ' + missing + (missing === 1 ? ' Produkt' : ' Produkte') + ' – in eine Kult-Box gehören ' + this.min + ' bis ' + this.max + '.';
      else this.statusEl.textContent = free === 0 ? 'Deine Box ist voll.' : 'Noch ' + free + (free === 1 ? ' Platz frei' : ' Plätze frei');
      if (this.autofillNote) this.statusEl.textContent = this.autofillNote; // Hinweis der Budget-Zusammenstellung bleibt bis zur nächsten Änderung
      this.addBtn.disabled = this.items.length === 0 && !this.card;
      const bar = this.querySelector('[data-gb-bar]');
      if (bar) {
        /* Mobil nur sinnvoll, solange die Produktauswahl offen ist (Rückweg „Zur Box“) */
        bar.hidden = (this.items.length === 0 && !this.card) || (this.isMobile() && !this.chooserOpen);
        bar.querySelector('[data-gb-bar-count]').textContent = this.items.length + ' von ' + this.max;
        bar.querySelector('[data-gb-bar-total]').textContent = money(total);
      }
      const hasCoffee = this.items.some((it) => it.variants);
      if (this.grindNote) {
        /* Vollautomat/Siebträger: Hinweis „ganze Bohnen“ (Setting bean_grinds/bean_note, 29.09.2026) */
        const beanGrinds = (this.dataset.beanGrinds || '').split(',').map((g) => g.trim()).filter(Boolean);
        const beanNote = beanGrinds.indexOf(this.grind) !== -1 && this.dataset.beanNote ? ' – ' + this.dataset.beanNote : '';
        this.grindNote.hidden = !hasCoffee;
        this.grindNote.textContent = hasCoffee ? (this.grind ? 'Zubereitung für alle Kaffees: ' + this.grind + beanNote : 'Zubereitung wählen – für deine Kaffees fehlt sie noch') : '';
        this.grindNote.classList.toggle('is-warn', hasCoffee && !this.grind);
      }
      this.renderShipping(total);
      this.syncUrl();
    }

    /* Versandkosten-Fortschritt (Regel 01.10.2026): Warenkorb zählt mit. Mit Kult Status gilt die Grenze der Stufe bei jeder
       Bestellung (Gold 0 = immer); ohne Status die Neukunden-Grenze, auch mit Abo-Zeile im Warenkorb (Regel 09.10.2026:
       Braun erst mit der ersten bezahlten Abo-Bestellung; Liquid liefert freeClubCents = 0). Anzeige erst mit Inhalt in der Box. */
    renderShipping(total) {
      const wrap = this.querySelector('[data-gb-ship]');
      if (!wrap) return;
      const hasContent = this.items.length > 0 || !!this.card;
      /* Kult-Abo im Warenkorb + Präsent = gemischt (Entscheidung Philip 11.10.2026, ersetzt D6, Plan §11.9): eine Sendung, einmal
         Versand; versandfrei, wenn der ganze Warenwert die Status-Grenze erreicht (ohne Status mit Abo = Braun-Grenze freeMixCents)
         ODER das Abo die Gramm-Grenze; das Präsent reist mit */
      if (this.aboFreiG > 0 && this.aboG >= 0) {
        wrap.hidden = !hasContent;
        if (wrap.hidden) return;
        const tE = this.hasStatus ? this.freeStatusCents : (this.freeMixCents || this.freeCents);
        const sumE = total + this.cartShipCents;
        const restE = tE > 0 ? Math.max(0, tE - sumE) : 0;
        const restG = restE === 0 ? 0 : Math.max(0, this.aboFreiG - this.aboG);
        const pctG = restG === 0 ? 100 : Math.max(0, Math.min(100, Math.round(Math.max(this.aboG / this.aboFreiG, sumE / tE) * 100)));
        const fmtG = (g) => (g > 0 && g % 1000 === 0 ? (g / 1000) + '\u00a0kg' : Math.round(g).toLocaleString('de-DE') + '\u00a0g');
        const tA = wrap.querySelector('[data-gb-ship-text]');
        const bA = wrap.querySelector('[data-gb-ship-bar]');
        const fA = wrap.querySelector('[data-gb-ship-fill]');
        if (tA) tA.innerHTML = restG > 0
          ? 'Noch <strong>' + money(restE) + '</strong> Warenwert oder <strong>' + fmtG(restG) + '</strong> Abo-Kaffee bis versandfrei – dein Präsent reist mit'
          : '<strong>Diese Lieferung versandfrei</strong> – dein Präsent reist mit';
        wrap.classList.toggle('is-done', restG === 0);
        if (bA) bA.setAttribute('aria-valuenow', String(pctG));
        if (fA) fA.style.width = pctG + '%';
        return;
      }
      let threshold = this.freeCents;
      let why = '';
      if (this.hasStatus) { threshold = this.freeStatusCents; why = this.dataset.statusStufe ? ' mit Kult Status ' + this.dataset.statusStufe : ' mit deinem Kult Status'; }
      else if (this.cartHasClub && this.freeClubCents > 0) { threshold = this.freeClubCents; why = ' mit deinem Kult-Abo im Warenkorb'; }
      const alwaysFree = this.hasStatus && threshold === 0;
      wrap.hidden = !hasContent || (!threshold && !alwaysFree);
      if (wrap.hidden) return;
      if (alwaysFree) {
        const t = wrap.querySelector('[data-gb-ship-text]');
        const b = wrap.querySelector('[data-gb-ship-bar]');
        const f = wrap.querySelector('[data-gb-ship-fill]');
        if (t) t.innerHTML = '<strong>Versandkostenfrei</strong> – mit Kult Status Gold immer versandfrei';
        wrap.classList.add('is-done');
        if (b) b.setAttribute('aria-valuenow', '100');
        if (f) f.style.width = '100%';
        return;
      }
      /* Gold mit Kult-Abo: Euro-Grenze auf den ganzen versandpflichtigen Warenkorb (inkl. Abo) */
      const orderTotal = total + (this.cartHasClub ? this.cartShipCents : this.cartTotal);
      const rest = Math.max(0, threshold - orderTotal);
      const pct = Math.max(0, Math.min(100, Math.round((orderTotal / threshold) * 100)));
      const cartNote = this.cartTotal > 0 ? ' – dein Warenkorb (' + money(this.cartTotal) + ') zählt mit' : '';
      const text = wrap.querySelector('[data-gb-ship-text]');
      const barEl = wrap.querySelector('[data-gb-ship-bar]');
      const fill = wrap.querySelector('[data-gb-ship-fill]');
      if (text) text.innerHTML = rest > 0
        ? 'Noch <strong>' + money(rest) + '</strong> bis zum kostenlosen Versand (ab ' + moneyShort(threshold) + why + ')' + cartNote
        : '<strong>Versandkostenfrei</strong> – dein Versand ist gratis' + (why ? ' (ab ' + moneyShort(threshold) + why + ')' : '');
      wrap.classList.toggle('is-done', rest === 0);
      if (barEl) barEl.setAttribute('aria-valuenow', String(pct));
      if (fill) fill.style.width = pct + '%';
    }

    syncUrl() {
      const url = new URL(window.location.href);
      if (this.items.length) url.searchParams.set('box', this.items.map((it) => it.handle).join(',')); else url.searchParams.delete('box');
      if (this.items.some((it) => it.variants) && this.grind) url.searchParams.set('zub', this.grind); else url.searchParams.delete('zub');
      if (this.card) url.searchParams.set('karte', this.card.handle); else url.searchParams.delete('karte');
      if (this.occasion) url.searchParams.set('anlass', this.occasion); else url.searchParams.delete('anlass');
      window.history.replaceState(null, '', url.toString());
    }

    restoreGrindFromUrl() {
      const zub = this.initialParams.get('zub');
      if (!zub) return;
      const btn = Array.from(this.querySelectorAll('[data-gb-grind]')).find((b) => b.dataset.gbGrind === zub);
      if (btn) this.setGrind(btn);
    }

    restoreFromUrl() {
      const clean = (h) => h.replace(/[^a-z0-9-]/g, '');
      const param = this.initialParams.get('box');
      if (param) {
        param.split(',').forEach((handle) => {
          const btn = this.querySelector('[data-gb-add][data-kind="item"][data-handle="' + clean(handle) + '"]');
          if (btn && this.items.length < this.max) this.items.push(this.entryFromButton(btn));
        });
      }
      const karte = this.initialParams.get('karte');
      if (karte) {
        const cardBtn = this.querySelector('[data-gb-add][data-kind="card"][data-handle="' + clean(karte) + '"]');
        if (cardBtn) this.card = this.entryFromButton(cardBtn);
      }
      const anlass = this.initialParams.get('anlass');
      /* ?anlass= nimmt Label („Alles Gute“) oder Key („alles-gute“, Tag anlass_<key>) */
      const hit = Array.from(this.querySelectorAll('[data-gb-occasion]')).find((b) => b.dataset.gbOccasion === anlass || occasionKey(b.dataset.gbOccasion) === anlass);
      this.setOccasion(hit ? hit.dataset.gbOccasion : this.occasion);
      // ?budget=49 (Euro, brutto, ohne Versand): Box automatisch so nah wie möglich am Wunschpreis füllen (01.10.2026)
      const budget = parseFloat((this.initialParams.get('budget') || '').replace(',', '.'));
      if (!param && budget > 0 && !this.items.length) this.autofill(Math.round(budget * 100));
    }

    // Vorschlagslogik wie assets/kult-b2b-builder.js (runSuggestion), nur brutto: Runde 1 je Kategorie (Reihenfolge der
    // Tabs, Kaffee zuerst) das teuerste Produkt, das noch ins Restbudget passt; Runde 2 bis max. Plätze auffüllen, höchstens
    // zwei je Kategorie; Feinschliff: Tausch gegen ein teureres Produkt derselben Kategorie, solange der Rest kleiner wird.
    // Gutscheine und Grußkarten bleiben außen vor. Budget = Box + Produkte, Versand ist nicht enthalten.
    autofill(budgetCents) {
      const buttons = Array.from(this.querySelectorAll('[data-gb-add][data-kind="item"]'))
        .filter((b) => !b.disabled && !/^gutschein-/.test(b.dataset.handle || ''));
      const prods = buttons.map((b) => this.entryFromButton(b)).filter((p) => p.price > 0 && p.handle);
      if (!prods.length) return;
      const cheapest = Math.min.apply(null, prods.map((p) => p.price));
      const minSum = prods.map((p) => p.price).sort((a, b) => a - b).slice(0, this.min).reduce((a, b) => a + b, 0);
      let R = budgetCents - this.boxPrice;
      if (R < minSum) {
        this.flash('Ab ' + money(this.boxPrice + minSum) + ' stellen wir eine Box zusammen. Wähle unten selbst aus.');
        return;
      }
      // Für den Mindestinhalt Budget zurückhalten: je noch fehlendem Platz den günstigsten Preis
      const reserve = (n) => Math.max(0, this.min - n - 1) * cheapest;
      const cats = [];
      prods.forEach((p) => { if (cats.indexOf(p.cat) === -1) cats.push(p.cat); });
      const used = {}, count = {}, result = [];
      const pickMax = (pool) => pool.reduce((best, p) => (!best || p.price > best.price ? p : best), null);
      cats.forEach((cat) => {
        if (result.length >= this.max) return;
        const pick = pickMax(prods.filter((p) => p.cat === cat && !used[p.handle] && p.price <= R - reserve(result.length)));
        if (pick) { result.push(pick); used[pick.handle] = true; R -= pick.price; count[cat] = 1; }
      });
      let guard = 0;
      while (result.length < this.max && guard++ < 50) {
        const next = pickMax(prods.filter((p) => !used[p.handle] && p.price <= R - reserve(result.length) && ((count[p.cat] || 0) < 2 || result.length < this.min)));
        if (!next) break;
        result.push(next); used[next.handle] = true; R -= next.price; count[next.cat] = (count[next.cat] || 0) + 1;
      }
      let improved = true, rounds = 0;
      while (improved && rounds++ < 10) {
        improved = false;
        result.forEach((cur, i) => {
          const cand = pickMax(prods.filter((p) => p.cat === cur.cat && !used[p.handle] && p.price > cur.price && p.price - cur.price <= R));
          if (cand) { used[cur.handle] = false; used[cand.handle] = true; R -= cand.price - cur.price; result[i] = cand; improved = true; }
        });
      }
      if (result.length < this.min) {
        this.flash('Für dieses Budget passen keine ' + this.min + ' Produkte in die Box. Wähle unten selbst aus.');
        return;
      }
      this.items = result;
      this.render();
      const sum = this.boxPrice + result.reduce((acc, p) => acc + p.price, 0);
      this.autofillNote = 'Für ' + money(budgetCents) + ' zusammengestellt: ' + money(sum) + ' ohne Versand. Tausche Produkte, wie du magst.';
      if (this.statusEl) this.statusEl.textContent = this.autofillNote;
    }

    flash(msg) {
      this.errorEl.textContent = msg;
      this.errorEl.hidden = !msg;
    }

    submit() {
      if (!this.items.length && !this.card) return;
      const needsGrind = this.items.some((it) => it.variants) && !this.grind;
      if (needsGrind) {
        this.flash('Bitte wähle noch die Zubereitung für deine Kaffees – in der Kategorie Kaffee.');
        this.jumpToGrind();
        return;
      }
      if (this.items.length < this.min) {
        this.flash('Lege mindestens ' + this.min + ' Produkte in die Box – eine Kult-Box hat ' + this.min + ' bis ' + this.max + ' Produkte.');
        return;
      }
      const group = 'KB-' + Date.now().toString(36).toUpperCase();
      const contents = this.items.map((it) => it.title).concat(this.card ? [this.card.title] : []).join(', ');
      const hasCoffee = this.items.some((it) => it.variants);
      const boxProps = { _kultbox: group, 'Kult-Box': 'Verpackung · Inhalt: ' + contents };
      boxProps['Anlass'] = this.occasion || this.occasionDefault;
      if (hasCoffee) boxProps['Zubereitung'] = this.grind;
      const lines = [{ id: this.boxVariantId, quantity: 1, properties: boxProps }];
      this.items.forEach((it, i) => {
        const props = { _kultbox: group, 'Kult-Box': 'Inhalt ' + (i + 1) + ' von ' + this.items.length };
        if (it.variants) props['Zubereitung'] = this.grind;
        lines.push({ id: it.variant, quantity: 1, properties: props });
      });
      if (this.card) {
        const cardProps = { _kultbox: group, 'Kult-Box': 'Grußkarte' };
        if (this.greeting.trim()) cardProps['Grußtext'] = this.greeting.trim();
        lines.push({ id: this.card.variant, quantity: 1, properties: cardProps });
      }
      this.flash('');
      this.addBtn.disabled = true;
      this.addBtn.classList.add('is-loading');
      fetch(this.dataset.addUrl + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: lines })
      })
        .then((r) => r.json().then((data) => ({ ok: r.ok, data: data })))
        .then((res) => {
          if (!res.ok) throw new Error(res.data.description || res.data.message || 'Fehler');
          return fetch('/cart.js').then((r) => r.json());
        })
        .then((cart) => {
          document.dispatchEvent(new CustomEvent('kult:cart:add', { detail: { cart: cart, title: this.boxProductTitle || this.dataset.boxTitle } }));
          this.items = [];
          this.card = null;
          this.greeting = '';
          if (this.greetingInput) this.greetingInput.value = '';
          if (this.greetingCount) this.greetingCount.textContent = '0/500 Zeichen';
          this.render();
        })
        .catch((err) => this.flash('Das hat leider nicht geklappt: ' + err.message))
        .finally(() => { this.addBtn.classList.remove('is-loading'); this.addBtn.disabled = this.items.length === 0 && !this.card; });
    }
  }

  if (!customElements.get('kult-gift-builder')) customElements.define('kult-gift-builder', KultGiftBuilder);
})();
